/**
 * Core animation library:
 *
 * https://www.figma.com/file/zYke5qihweC8yQSpXpcRO2/%F0%9F%93%9000-OM-CORE-Library?node-id=1063%3A4447
 */

// ============================================================================
// README
// ----------------------------------------------------------------------------
// The following objects are taken directly from the CORE figma diagram.
// No additions or changes should be made here, except to align with the CORE
// diagram.
// Changes to the CORE diagram should go through the process defined in RFC009.
// ============================================================================

import React, { useContext } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import { IMAGE_URL } from '@ordermentum/ui-constants';
import { Theme, ThemeContext } from '../../utilities/theme';

/*
 * To add new animations:
 * 1. Download relevant lottie json files
 * 2. Save them to static/images/lottie/
 * 3. Change the filetype from .json to .lottie (cause json imports are weird)
 * 4. Add require(filename) to static/supplier/index.js
 * 5. Add an object with at minimum, a "default" key to the ANIMATIONS object
 *
 * Naming of the animations should be snakeCase, based on the name provided in
 * the figma documentation.
 */
const ANIMATIONS = {
  email: {
    futuro: 'emailLight.lottie',
    default: 'emailLight.lottie',
    'futuro-dark': 'emailDark.lottie',
  },
  success: {
    futuro: 'successLight.lottie',
    default: 'successLight.lottie',
    'futuro-dark': 'successDark.lottie',
  },
  emptyState: {
    futuro: 'emptyStateLight.lottie',
    default: 'emptyStateLight.lottie',
    'futuro-dark': 'emptyStateDark.lottie',
  },
  trophy: {
    futuro: 'trophy.lottie',
    default: 'trophy.lottie',
  },
  organised: {
    futuro: 'organised.lottie',
    default: 'organised.lottie',
  },
};

export const animationNames = Object.keys(ANIMATIONS);

export type animationNameTypes = keyof typeof ANIMATIONS;

export type AnimationProps = {
  name: animationNameTypes;
  loop?: boolean;
  size?: string;
};

const getSrc = (theme: Theme, name: animationNameTypes) => {
  const src = ANIMATIONS[name];
  return src[theme] || src.default;
};

/**
 * A futuro animation.
 *
 * @argument name string - the name of the animation as defined in futuro docs
 * @argument loop boolean - whether the animation loops or plays once. Default false.
 *    note, there's an issue with the lottie player where changing loop after the
 *    value has been set doesn't actually change the loop setting.
 *
 * @example
 *
 * <Animation name="success" />
 * <Animation name="email" loop />
 */
const Animation = ({ name, loop = false, size }: AnimationProps) => {
  if (!(name in ANIMATIONS)) {
    throw new Error(`Unknown animation ${name}`);
  }
  const theme = useContext(ThemeContext);
  const src = `${IMAGE_URL}/lottie/${getSrc(theme, name)}`;
  const customHeight = size === 'L' ? '11rem' : '4rem';
  return (
    <Player
      src={src}
      style={{ height: `${customHeight}` }}
      loop={loop}
      autoplay
      keepLastFrame
    />
  );
};

export default Animation;
